import React, { useState, useEffect } from "react";
import { Alert, Row, Col, Card, Collapse, Input, Space, Button, Select, Table, Tabs, Tag, Tooltip, Popconfirm } from "antd";
import { SyncOutlined, CheckCircleOutlined, UndoOutlined } from "@ant-design/icons";
import { useLocation } from "react-router-dom";
import dayjs from "dayjs";
import useMaterialApprovals from "../../../hooks/academics/useMaterialApprovals";
import useInstructionalMaterials from "../../../hooks/academics/useInstructionalMaterials";
import ValuesStore from "../../../store/values-store";
import useAcademicYears from "../../../hooks/common/useAcademicYears";
import useTerms from "../../../hooks/common/useTerms";
import ApprovalQueueTable from "../../../components/academics/materials/ApprovalQueueTable";

const ROLE_CONFIG = {
  hod: {
    title: "HoD Review Queue",
    stage: "HOD",
  },
  section_head: {
    title: "Section Head Review Queue",
    stage: "SECTION_HEAD",
  },
  deputy_head: {
    title: "Deputy Head Approval & Unlock Queue",
    stage: "DEPUTY_HEAD",
  },
  headteacher: {
    title: "Headteacher — Unlock Queue",
    stage: "TO_BE_UNLOCKED",
  },
};

const STAGE_BY_ROLE = {
  hod: "HOD",
  section_head: "SECTION_HEAD",
  deputy_head: "DEPUTY_HEAD",
};

const MaterialsApproval = ({ role }) => {
  const location = useLocation();
  const config = ROLE_CONFIG[role] || { title: "Approval Queue", stage: "" };

  const {
    queue,
    queueMeta,
    loading,
    fetchQueue,
    createApproval,
    unlockMaterial,
  } = useMaterialApprovals();
  const { confirmDelivery, reverseDelivery } = useInstructionalMaterials();
  const { academicYears, fetchAcademicYears } = useAcademicYears();
  const { terms, fetchTerms } = useTerms();

  const [filterAcadyrID, setFilterAcadyrID] = useState(null);
  const [filterTermID, setFilterTermID] = useState(null);
  const [selectedDeptIds, setSelectedDeptIds] = useState([]);
  const [deptClassSearch, setDeptClassSearch] = useState("");

  const isHodView = role === "hod";
  const isSectionHeadView = role === "section_head";
  const isReviewerView = isHodView || isSectionHeadView;
  const deliveryTabLabel = "Delivery materials";
  const reviewQueue = isHodView
    ? queue.filter((item) => item.status === "Pending HoD Review")
    : isSectionHeadView
    ? queue.filter(
        (item) => item.status === "Pending Section Head Validation",
      )
    : queue;
  const deptMaterials =
    isHodView || isSectionHeadView
      ? queue.filter((item) => item.status === "Unlocked")
      : [];
  // Delivery confirmation is role-specific: HODs handle non-PreSchool
  // materials, Section Heads handle PreSchool materials.
  const store = ValuesStore();
  const myRoleNames = store.getValue("myRoles");
  const isDeliveryHod = (Array.isArray(myRoleNames) ? myRoleNames : []).some(
    (r) => {
      const name = String(
        typeof r === "string" ? r : r?.name || "",
      ).toLowerCase();
      return (
        name.includes("head of department") ||
        name.trim() === "hod" ||
        name.includes("(hod)") ||
        name.startsWith("hod (")
      );
    },
  );
  const isDeliverySectionHead = (
    Array.isArray(myRoleNames) ? myRoleNames : []
  ).some((r) => {
    const name = String(
      typeof r === "string" ? r : r?.name || "",
    ).toLowerCase();
    return name.includes("head of section") || name.includes("section head");
  });
  const isDeliveryReviewer = isHodView
    ? isDeliveryHod
    : isSectionHeadView
    ? isDeliverySectionHead
    : false;

  useEffect(() => {
    fetchAcademicYears();
    fetchTerms();
  }, [fetchAcademicYears, fetchTerms]);

  useEffect(() => {
    setFilterAcadyrID(null);
    setFilterTermID(null);
  }, [location.pathname]);

  const buildParams = () => {
    const params = {};
    if (filterAcadyrID) params.acadyrID = filterAcadyrID;
    if (filterTermID) params.termID = filterTermID;
    return params;
  };

  useEffect(() => {
    fetchQueue(role, buildParams());
  }, [role, filterAcadyrID, filterTermID]);

  const refresh = () => fetchQueue(role, buildParams());

  const handleApprove = async (materialId, remarks) => {
    const ok = await createApproval({
      materialID: materialId,
      stage: STAGE_BY_ROLE[role],
      action: "Approved",
      remarks: remarks || "",
    });
    if (ok) refresh();
    return ok;
  };

  const handleReject = async (materialId, remarks) => {
    const ok = await createApproval({
      materialID: materialId,
      stage: STAGE_BY_ROLE[role],
      action: "Rejected",
      remarks,
    });
    if (ok) refresh();
    return ok;
  };

  const handleUnlock = async (materialId) => {
    const ok = await unlockMaterial(materialId);
    if (ok) refresh();
    return ok;
  };

  const handleConfirmDelivery = async (record) => {
    const updated = await confirmDelivery(record.id);
    if (updated) refresh();
  };

  const handleReverseDelivery = async (record) => {
    const updated = await reverseDelivery(record.id);
    if (updated) refresh();
  };

  const deliverable = (record) =>
    isDeliveryReviewer && record.status === "Unlocked" && !record.isDelivered;

  const handleConfirmMany = async (records) => {
    const targets = (records || []).filter(deliverable);
    if (!targets.length) return;
    for (const record of targets) {
      await confirmDelivery(record.id);
    }
    setSelectedDeptIds([]);
    refresh();
  };

  const groupByClass = (materials) => {
    const groups = new Map();
    (materials || []).forEach((material) => {
      const key = material.className || "Unassigned class";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(material);
    });
    return Array.from(groups.entries()).sort(([left], [right]) =>
      left.localeCompare(right),
    );
  };

  const deptColumns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      sorter: (a, b) => (a.title || "").localeCompare(b.title || ""),
    },
    {
      title: "Teacher",
      dataIndex: "teacherName",
      key: "teacherName",
      render: (v) => v || "-",
    },
    {
      title: "Subject",
      dataIndex: "subjectName",
      key: "subjectName",
      render: (v) => v || "-",
    },
    {
      title: "Term",
      dataIndex: "term",
      key: "term",
      render: (v) => v || "-",
    },
    {
      title: "Delivered",
      key: "delivered",
      align: "center",
      render: (_, record) => {
        if (record.isDelivered) {
          const tip = [
            record.deliveredAt
              ? dayjs(record.deliveredAt).format("DD MMM YYYY HH:mm")
              : null,
            record.deliveredByName || null,
          ]
            .filter(Boolean)
            .join(" · ");
          const badge = <Tag color="green">Delivered</Tag>;
          return tip ? <Tooltip title={tip}>{badge}</Tooltip> : badge;
        }
        return <Tag color="default">Not delivered</Tag>;
      },
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, record) => (
        <Space size="small">
          {isDeliveryReviewer &&
            record.status === "Unlocked" &&
            !record.isDelivered && (
              <Tooltip title="Confirm delivery">
                <Popconfirm
                  title="Confirm delivery of this material?"
                  description="This marks the material as delivered."
                  onConfirm={() => handleConfirmDelivery(record)}
                  okText="Confirm"
                  cancelText="Cancel"
                >
                  <Button
                    type="text"
                    style={{ color: "#52c41a" }}
                    icon={<CheckCircleOutlined />}
                  />
                </Popconfirm>
              </Tooltip>
            )}
          {isDeliveryReviewer && record.isDelivered && (
            <Tooltip title="Reverse delivery">
              <Popconfirm
                title="Reverse the delivery mark?"
                description="This clears the delivered mark on this material."
                onConfirm={() => handleReverseDelivery(record)}
                okText="Reverse"
                okButtonProps={{ danger: true }}
                cancelText="Cancel"
              >
                <Button type="text" danger icon={<UndoOutlined />} />
              </Popconfirm>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const filterControls = (
    <Row gutter={12} style={{ marginBottom: 16 }}>
      <Col xs={24} sm={12} md={5}>
        <Select
          placeholder="Academic Year"
          allowClear
          style={{ width: "100%" }}
          value={filterAcadyrID}
          onChange={setFilterAcadyrID}
          showSearch
          optionFilterProp="label"
        >
          {academicYears.map((y) => (
            <Select.Option key={y.id} value={y.id} label={y.acadyr}>
              {y.acadyr}
            </Select.Option>
          ))}
        </Select>
      </Col>
      <Col xs={24} sm={12} md={5}>
        <Select
          placeholder="Term"
          allowClear
          style={{ width: "100%" }}
          value={filterTermID}
          onChange={setFilterTermID}
          showSearch
          optionFilterProp="label"
        >
          {terms.map((t) => (
            <Select.Option key={t.id} value={t.id} label={t.name}>
              {t.name}
            </Select.Option>
          ))}
        </Select>
      </Col>
    </Row>
  );

  const reviewCard = (
    <Card
      title={config.title}
      extra={
        <Space>
          <Button icon={<SyncOutlined />} onClick={refresh} loading={loading}>
            Refresh
          </Button>
        </Space>
      }
    >
      <ApprovalQueueTable
        queue={reviewQueue}
        loading={loading}
        role={role}
        onApprove={handleApprove}
        onReject={handleReject}
        onUnlock={handleUnlock}
      />
    </Card>
  );

  const deptClassGroups = groupByClass(deptMaterials);

  const normalizedSearch = deptClassSearch.trim().toLowerCase();
  const filteredDeptClassGroups = normalizedSearch
    ? deptClassGroups.filter(([className]) =>
        className.toLowerCase().includes(normalizedSearch),
      )
    : deptClassGroups;

  const deptCard = (
    <Card
      title={deliveryTabLabel}
      extra={
        <Space wrap>
          <Button
            icon={<CheckCircleOutlined />}
            disabled={!isDeliveryReviewer}
            onClick={() => handleConfirmMany(deptMaterials)}
          >
            Mark all as delivered
          </Button>
          <Button
            icon={<SyncOutlined />}
            onClick={refresh}
            loading={loading}
          >
            Refresh
          </Button>
        </Space>
      }
    >
      <Input.Search
        allowClear
        placeholder="Filter by class name"
        value={deptClassSearch}
        onChange={(event) => setDeptClassSearch(event.target.value)}
        style={{ maxWidth: 320, marginBottom: 12 }}
      />
      {filteredDeptClassGroups.length === 0 ? (
        <Table
          rowKey="id"
          columns={deptColumns}
          dataSource={[]}
          loading={loading}
          pagination={false}
          size="small"
        />
      ) : (
        <Collapse
          items={filteredDeptClassGroups.map(([className, materials]) => {
            const deliverableRows = materials.filter(deliverable);
            const deliveredCount = materials.filter(
              (item) => item.isDelivered,
            ).length;
            return {
              key: className,
              label: (
                <Space wrap>
                  <span style={{ fontWeight: 500 }}>{className}</span>
                  <Tag>{`${materials.length} material(s)`}</Tag>
                  <Tag color={deliveredCount === materials.length ? "green" : "default"}>
                    {`${deliveredCount}/${materials.length} delivered`}
                  </Tag>
                </Space>
              ),
              extra: (
                <Button
                  size="small"
                  disabled={!deliverableRows.length}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleConfirmMany(materials);
                  }}
                >
                  {`Mark class delivered (${deliverableRows.length})`}
                </Button>
              ),
              children: (
                <Table
                  rowKey="id"
                  columns={deptColumns}
                  dataSource={materials}
                  loading={loading}
                  pagination={false}
                  size="small"
                  scroll={{ x: 800 }}
                  rowSelection={{
                    selectedRowKeys: selectedDeptIds,
                    onChange: (keys) => setSelectedDeptIds(keys),
                    getCheckboxProps: (record) => ({
                      disabled: !deliverable(record),
                    }),
                  }}
                />
              ),
            };
          })}
        />
      )}
      {isDeliveryReviewer && selectedDeptIds.length > 0 && (
        <Space style={{ marginTop: 12 }}>
          <Button
            type="primary"
            icon={<CheckCircleOutlined />}
            onClick={() =>
              handleConfirmMany(
                deptMaterials.filter((item) =>
                  selectedDeptIds.includes(item.id),
                ),
              )
            }
          >
            {`Mark selected delivered (${selectedDeptIds.length})`}
          </Button>
          <Button onClick={() => setSelectedDeptIds([])}>Clear</Button>
        </Space>
      )}
    </Card>
  );

  return (
    <div style={{ padding: 24 }}>
      {isHodView && queueMeta?.hasDepartment === false ? (
        <Alert
          type="warning"
          showIcon
          message="No department assigned"
          description={queueMeta.message}
          style={{ marginBottom: 16 }}
        />
      ) : (
        <>
          {filterControls}
          {isReviewerView ? (
            <Tabs
              defaultActiveKey="queue"
              items={[
                { key: "queue", label: "Review Queue", children: reviewCard },
                {
                  key: "department",
                  label: deliveryTabLabel,
                  children: deptCard,
                },
              ]}
            />
          ) : (
            reviewCard
          )}
        </>
      )}
    </div>
  );
};

export default MaterialsApproval;
